import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Trophy, Timer, Loader2, ArrowRight, ArrowLeft,
  MessageCircle, Download, BadgeCheck, AlertCircle, Gift,
} from "lucide-react";
import { LOGO, WHATSAPP_LINK, COURSES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAMS = COURSES.map((c) => c.title).filter((t) => !/^(BHMCT|BSc)/i.test(t));

const inputClass =
  "w-full rounded-md border border-brand-line bg-brand-bg px-4 py-3 text-sm text-brand-ink outline-none transition-colors duration-300 placeholder:text-brand-muted/60 focus:border-brand-green";

export function PageHeader() {
  return (
    <header className="border-b border-brand-line bg-brand-bg/80 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between px-6">
        <a href="/" data-testid="scholarship-logo" className="flex items-center gap-3">
          <img src={LOGO} alt="Abhi Institute of Hotel Management" className="h-10 w-auto" />
        </a>
        <a
          data-testid="back-to-site-link"
          href="/"
          className="text-sm font-semibold text-brand-green transition-colors hover:text-brand-terra"
        >
          ← Back to Website
        </a>
      </div>
    </header>
  );
}

function StepIndicator({ step }) {
  const steps = ["Register", "Exam", "Result"];
  return (
    <div data-testid="step-indicator" className="flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-[0.16em]">
      {steps.map((label, i) => (
        <span key={label} className="flex items-center gap-3">
          <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] ${
            step > i + 1 ? "border-brand-green bg-brand-green text-white" : step === i + 1 ? "border-brand-terra bg-brand-terra text-white" : "border-brand-line text-brand-muted"
          }`}>
            {i + 1}
          </span>
          <span className={step >= i + 1 ? "text-brand-ink" : "text-brand-muted"}>{label}</span>
          {i < steps.length - 1 && <span className="text-brand-line">——</span>}
        </span>
      ))}
    </div>
  );
}

function RegisterStep({ onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", program: PROGRAMS[0], agreed: false });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agreed) {
      toast.error("Please agree to take the 20-minute timed assessment.");
      return;
    }
    setSending(true);
    try {
      const { data: reg } = await axios.post(`${API}/scholarship/register`, form);
      const { data } = await axios.get(`${API}/scholarship/exam/${reg.exam_token}`);
      onDone(reg, data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.form
      data-testid="register-form"
      onSubmit={submit}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: EASE }}
      className="border border-brand-line bg-brand-surface p-8 shadow-sm md:p-10"
    >
      <h2 className="font-serif text-3xl font-light">Step 1 · Candidate Registration</h2>
      <div className="mt-8 grid gap-5 md:grid-cols-2">
        <div>
          <label htmlFor="reg-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">Full Name *</label>
          <input id="reg-name" data-testid="reg-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputClass} />
        </div>
        <div>
          <label htmlFor="reg-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">Email Address *</label>
          <input id="reg-email" data-testid="reg-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputClass} />
        </div>
        <div>
          <label htmlFor="reg-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">WhatsApp Mobile Number *</label>
          <input id="reg-phone" data-testid="reg-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputClass} />
        </div>
        <div>
          <label htmlFor="reg-city" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">City / State *</label>
          <input id="reg-city" data-testid="reg-city-input" required value={form.city} onChange={set("city")} placeholder="e.g. New Delhi, Delhi" className={inputClass} />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="reg-program" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">Target Program *</label>
          <select id="reg-program" data-testid="reg-program-select" required value={form.program} onChange={set("program")} className={inputClass}>
            {PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <label className="flex items-start gap-3 md:col-span-2" htmlFor="reg-agree">
          <input id="reg-agree" data-testid="reg-agree-checkbox" type="checkbox" checked={form.agreed} onChange={set("agreed")} className="mt-1 h-4 w-4 accent-brand-green" />
          <span className="text-sm text-brand-muted">I agree to take the 20-minute timed assessment.</span>
        </label>
      </div>
      <button
        data-testid="start-exam-button"
        type="submit"
        disabled={sending}
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-brand-green px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-ink disabled:opacity-60"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Start Free Exam
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </motion.form>
  );
}

function ExamStep({ reg, exam, onDone }) {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(exam.questions.length).fill(null));
  const [timeLeft, setTimeLeft] = useState(exam.duration_seconds);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  useEffect(() => {
    const t = setInterval(() => setTimeLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  const submit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/scholarship/submit`, { token: reg.exam_token, answers: answersRef.current });
      onDone(data.result);
    } catch {
      toast.error("Submission failed. Please contact admissions on WhatsApp.");
      submittedRef.current = false;
      setSubmitting(false);
    }
  };
  const submitRef = useRef(submit);
  submitRef.current = submit;

  useEffect(() => {
    if (timeLeft === 0) submitRef.current(true);
  }, [timeLeft]);

  useEffect(() => {
    const block = (e) => e.preventDefault();
    document.addEventListener("contextmenu", block);
    return () => document.removeEventListener("contextmenu", block);
  }, []);

  const q = exam.questions[current];
  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");
  const answeredCount = answers.filter((a) => a !== null).length;

  const manualSubmit = () => {
    if (answeredCount < exam.questions.length) {
      toast.error(`Please answer all questions first (${answeredCount}/${exam.questions.length} answered).`);
      return;
    }
    submit();
  };

  return (
    <div
      data-testid="exam-section"
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      className="select-none"
    >
      <div className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-4 border border-brand-line bg-brand-surface/95 px-6 py-4 shadow-sm backdrop-blur-xl">
        <div>
          <p className="font-serif text-lg text-brand-ink">{exam.candidate.name}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-muted">{exam.candidate.program}</p>
        </div>
        <div className="flex items-center gap-6">
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-brand-muted">
            Question {current + 1} of {exam.questions.length}
          </p>
          <p data-testid="exam-timer" className={`flex items-center gap-2 rounded-full px-4 py-2 font-mono text-lg font-semibold ${timeLeft < 120 ? "bg-red-50 text-red-600" : "bg-brand-green/10 text-brand-green"}`}>
            <Timer className="h-4 w-4" />
            {mm}:{ss}
          </p>
        </div>
      </div>

      <div className="mt-2 h-1 w-full bg-brand-line">
        <div className="h-1 bg-brand-terra transition-[width] duration-300" style={{ width: `${((current + 1) / exam.questions.length) * 100}%` }} />
      </div>

      <motion.div
        key={current}
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="mt-10 border border-brand-line bg-brand-surface p-8 shadow-sm md:p-10"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-terra">Question {q.n}</p>
        <h2 data-testid="exam-question" className="mt-4 font-serif text-2xl font-light leading-snug text-brand-ink md:text-3xl">
          {q.q}
        </h2>
        <div className="mt-8 space-y-3">
          {q.options.map((opt, oi) => {
            const selected = answers[current] === oi;
            return (
              <button
                key={oi}
                type="button"
                data-testid={`option-${oi}`}
                onClick={() => setAnswers(answers.map((a, i) => (i === current ? oi : a)))}
                className={`flex w-full items-center gap-4 rounded-md border px-5 py-4 text-left text-sm transition-colors duration-200 md:text-base ${
                  selected ? "border-brand-green bg-brand-green/5 text-brand-ink" : "border-brand-line bg-brand-bg text-brand-muted hover:border-brand-green/50"
                }`}
              >
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-brand-green" : "border-brand-line"}`}>
                  {selected && <span className="h-2.5 w-2.5 rounded-full bg-brand-green" />}
                </span>
                <span className="font-mono text-xs text-brand-terra">{String.fromCharCode(65 + oi)}</span>
                {opt}
              </button>
            );
          })}
        </div>
        <div className="mt-10 flex items-center justify-between">
          <button
            type="button"
            data-testid="exam-prev-button"
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={current === 0}
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-muted transition-colors hover:text-brand-ink disabled:opacity-40"
          >
            <ArrowLeft className="h-4 w-4" /> Previous
          </button>
          {current < exam.questions.length - 1 ? (
            <button
              type="button"
              data-testid="exam-next-button"
              onClick={() => setCurrent((c) => c + 1)}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-ink"
            >
              Next <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="exam-submit-button"
              onClick={manualSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-full bg-brand-terra px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-ink disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
              Submit Exam
            </button>
          )}
        </div>
      </motion.div>
      <p className="mt-4 flex items-center gap-2 text-xs text-brand-muted">
        <AlertCircle className="h-3.5 w-3.5" />
        Right-click and copy-paste are disabled. The exam auto-submits when the timer ends.
      </p>
    </div>
  );
}

function loadLogo() {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = "/images/logo-wide.png";
  });
}

const fmtDate = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
const generatedDate = () => new Date();
const validUntilDate = () => new Date(Date.now() + 10 * 86400000);

async function downloadCertificate(result) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = 297;
  const GREEN = [42, 75, 66], TERRA = [164, 92, 64], INK = [28, 28, 26], MUTED = [102, 102, 97], CREAM = [249, 249, 246];
  const generated = generatedDate();
  const validUntil = validUntilDate();

  doc.setFillColor(...CREAM);
  doc.rect(0, 0, W, 210, "F");
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(2.5);
  doc.rect(8, 8, W - 16, 194);
  doc.setDrawColor(...TERRA);
  doc.setLineWidth(0.5);
  doc.rect(12, 12, W - 24, 186);

  try {
    const logo = await loadLogo();
    doc.addImage(logo, "PNG", W / 2 - 26, 15, 52, 16.5);
  } catch { /* logo optional */ }

  doc.setTextColor(...TERRA);
  doc.setFont("times", "italic");
  doc.setFontSize(17);
  doc.text("Scholarship Award Certificate", W / 2, 42, { align: "center" });
  doc.setTextColor(...MUTED);
  doc.setFont("times", "normal");
  doc.setFontSize(10);
  doc.text("AIHM Scholarship Test · 2026", W / 2, 51, { align: "center" });

  doc.setTextColor(...INK);
  doc.setFontSize(11);
  doc.text("This is to certify that", W / 2, 70, { align: "center" });
  doc.setFont("times", "bold");
  doc.setFontSize(26);
  doc.text(result.name, W / 2, 85, { align: "center" });
  doc.setFont("times", "normal");
  doc.setFontSize(11);
  doc.text(`has scored ${result.score} out of ${result.total} in the AIHM Scholarship Test and is awarded the`, W / 2, 96, { align: "center" });
  doc.setTextColor(...TERRA);
  doc.setFont("times", "bold");
  doc.setFontSize(18);
  doc.text(result.tier_label.replace(/₹/g, "Rs. "), W / 2, 108, { align: "center" });

  doc.setDrawColor(...MUTED);
  doc.setLineWidth(0.3);
  doc.setLineDashPattern([1.5, 1.5], 0);
  doc.line(70, 117, W - 70, 117);
  doc.setLineDashPattern([], 0);

  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(`Redemption Code: ${result.redemption_code}`, W / 2, 126, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  doc.text(`Program: ${result.program}`, W / 2, 134, { align: "center" });
  doc.setTextColor(...GREEN);
  doc.setFont("helvetica", "bold");
  doc.text(`Generated: ${fmtDate(generated)}    ·    Valid Until: ${fmtDate(validUntil)} (10-day validity)`, W / 2, 142, { align: "center" });

  doc.setTextColor(...INK);
  doc.setFontSize(9.5);
  doc.text("Terms & Conditions", 40, 156);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  const terms = [
    `1. This scholarship is valid for 10 days from the date of certificate generation (valid until ${fmtDate(validUntil)}).`,
    "2. The scholarship amount will be adjusted only against the course fee and is not redeemable for cash.",
    "3. This certificate is non-transferable and valid exclusively for new admissions.",
    "4. Present this original certificate along with the redemption code at campus counselling.",
    "5. Abhi Institute of Hotel Management reserves the right to verify the candidate's identity before redemption.",
  ];
  terms.forEach((t, i) => doc.text(t, 40, 163 + i * 6));

  doc.setTextColor(...GREEN);
  doc.setFontSize(8);
  doc.text("E-1065, Dwarka Sector 07, Ramphal Chowk, New Delhi 110077 · +91-9971494317 · kchanda63@gmail.com", W / 2, 196, { align: "center" });

  doc.save(`ABHI-Scholarship-${result.redemption_code}.pdf`);
}

function ResultStep({ result }) {
  const generated = generatedDate();
  const validUntil = validUntilDate();
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
      <div className="text-center">
        <Trophy className="mx-auto h-12 w-12 text-brand-terra" />
        <h2 className="mt-4 font-serif text-4xl font-light md:text-5xl">
          You scored <em className="italic text-brand-terra">{result.score}/{result.total}</em>
        </h2>
        <p data-testid="result-tier" className="mt-3 text-lg font-semibold text-brand-green">{result.tier_label}</p>
        <p className="mt-2 text-sm text-brand-muted">A copy of your scorecard and certificate has been emailed to {result.email}.</p>
      </div>

      <div data-testid="certificate-card" className="mx-auto mt-10 max-w-2xl border-[6px] border-brand-green bg-brand-surface p-2 shadow-sm">
        <div className="border border-brand-terra/50 px-8 py-10 text-center">
          <img src="/images/logo-wide.png" alt="Abhi Institute of Hotel Management" className="mx-auto h-12 w-auto" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.24em] text-brand-muted">Abhi Institute of Hotel Management</p>
          <p className="mt-2 font-serif text-2xl italic text-brand-terra">Scholarship Award Certificate</p>
          <p className="mt-6 text-sm text-brand-muted">This is to certify that</p>
          <p data-testid="certificate-name" className="mt-1 font-serif text-3xl text-brand-ink">{result.name}</p>
          <p className="mt-3 text-sm text-brand-muted">
            scored <span className="font-semibold text-brand-ink">{result.score}/{result.total}</span> and is awarded the
          </p>
          <p className="mt-1 font-serif text-xl text-brand-green">{result.tier_label}</p>
          <div className="mx-auto mt-6 max-w-sm space-y-2 border-t border-dashed border-brand-line pt-5 text-left text-sm">
            <p className="flex justify-between gap-4"><span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-muted">Redemption Code</span><span data-testid="redemption-code" className="font-mono font-semibold text-brand-ink">{result.redemption_code}</span></p>
            <p className="flex justify-between gap-4"><span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-muted">Program</span><span className="text-brand-ink">{result.program}</span></p>
            <p className="flex justify-between gap-4"><span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-muted">Valid Until</span><span data-testid="certificate-validity" className="font-semibold text-brand-terra">{fmtDate(validUntil)}</span></p>
          </div>
          <div className="mt-6 border-t border-dashed border-brand-line pt-5 text-left">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-muted">Terms & Conditions</p>
            <ul className="mt-2 list-decimal space-y-1 pl-4 text-[11px] leading-relaxed text-brand-muted">
              <li>Valid for 10 days from the date of generation (until {fmtDate(validUntil)}).</li>
              <li>Scholarship amount is adjusted only against the course fee — not redeemable for cash.</li>
              <li>Non-transferable; valid exclusively for new admissions.</li>
              <li>Present this original certificate with the redemption code at campus counselling.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        <button
          data-testid="download-certificate-button"
          onClick={() => downloadCertificate(result)}
          className="inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-ink"
        >
          <Download className="h-4 w-4" /> Download Certificate (PDF)
        </button>
        <a
          data-testid="result-whatsapp-button"
          href={`${WHATSAPP_LINK.split("?text=")[0]}?text=${encodeURIComponent(`Hi, I just took the ABHI Scholarship Test and scored ${result.score}/${result.total}. My redemption code is ${result.redemption_code}.`)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-brand-green/30 px-7 py-3.5 text-sm font-semibold text-brand-green transition-colors hover:border-brand-green hover:bg-brand-green hover:text-white"
        >
          <MessageCircle className="h-4 w-4" /> Chat with Admissions on WhatsApp
        </a>
      </div>
    </motion.div>
  );
}

export default function ScholarshipExam() {
  const [step, setStep] = useState(1);
  const [reg, setReg] = useState(null);
  const [exam, setExam] = useState(null);
  const [result, setResult] = useState(null);

  const onRegistered = (regData, examData) => {
    setReg(regData);
    if (examData.already_submitted) {
      setResult(examData.result);
      setStep(3);
    } else {
      setExam(examData);
      setStep(2);
    }
    window.scrollTo(0, 0);
  };

  const onResult = (r) => {
    setResult(r);
    setStep(3);
    window.scrollTo(0, 0);
  };

  return (
    <div data-testid="scholarship-page" className="min-h-screen bg-brand-bg font-sans text-brand-ink antialiased">
      <PageHeader />

      {step === 1 && (
        <section className="bg-brand-green py-20 text-white md:py-24">
          <div className="mx-auto max-w-5xl px-6 text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-[#E8C4B0]/40 px-5 py-2 font-mono text-xs uppercase tracking-[0.22em] text-[#E8C4B0]"
            >
              <Trophy className="h-4 w-4" /> Free Exam · Scholarship up to ₹8,000
            </motion.p>
            <motion.h1
              data-testid="scholarship-headline"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="mt-7 font-serif text-4xl font-light leading-tight tracking-tight md:text-6xl"
            >
              AIHM <em className="italic text-[#E8C4B0]">Scholarship Test</em>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
              className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg"
            >
              Take the free 20-minute online assessment and get up to ₹8,000 in tuition
              scholarships for any Diploma or Degree program at Abhi Institute of Hotel Management.
              No registration fee, no payment required.
            </motion.p>
          </div>
        </section>
      )}

      <main className="mx-auto max-w-5xl px-6 py-14 md:py-20">
        <StepIndicator step={step} />

        {step === 1 && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-6 flex items-center gap-3 rounded-md border border-brand-green/30 bg-brand-green/5 px-5 py-4"
            >
              <Gift className="h-5 w-5 shrink-0 text-brand-green" />
              <p data-testid="free-exam-banner" className="text-sm font-semibold text-brand-green">100% Free — No registration fee. Register and start the exam instantly.</p>
            </motion.div>
            <div className="mt-12">
              <RegisterStep onDone={onRegistered} />
            </div>
          </>
        )}

        {step === 2 && reg && exam && (
          <ExamStep reg={reg} exam={exam} onDone={onResult} />
        )}

        {step === 3 && result && (
          <div className="mt-10">
            <ResultStep result={result} />
          </div>
        )}
      </main>

      <footer className="border-t border-brand-line py-8 text-center text-xs text-brand-muted">
        © 2026 Abhi Institute of Hotel Management · Dwarka, New Delhi · +91-9971494317
      </footer>
    </div>
  );
}
